import { ExpirationStatus, } from '@amzn/handshake-painter-typescript-client/v1';
import { generatePath } from 'react-router';
import { routes } from 'src/utils/routes';
/** Mapping of card type enums to localization keys for translation */
const VALID_CARD_TYPES_TRANSLATION = {
    CREDIT_CARD: 'creditCard',
    DEBIT_CARD: 'debitCard',
};
/**
 * Transforms raw payment selection response data into display-ready payment methods.
 *
 * Filters out invalid payment methods and logs errors for debugging purposes.
 * Each payment method is processed to include display-friendly information like
 * formatted card names, expiration dates, and validation status.
 *
 * @param rumClient - Analytics client for logging errors and events
 * @param paymentSelectionResponse - Raw payment selection data from API
 * @returns Array of filtered and formatted payment methods ready for display
 */
export const transformPaymentSelectionOutput = (rumClient, paymentSelectionResponse) => {
    if (!(paymentSelectionResponse === null || paymentSelectionResponse === void 0 ? void 0 : paymentSelectionResponse.paymentMethods)) {
        return [];
    }
    return paymentSelectionResponse.paymentMethods.reduce((validPaymentList, paymentMethod) => {
        try {
            validPaymentList.push(getDisplayReadyPaymentMethod(paymentMethod));
        }
        catch (error) {
            rumClient.recordEvent('INVALID_PAYMENT_METHOD', {
                errorMessage: 'Payment method missing necessary fields: ' + error,
                paymentMethod: paymentMethod,
            });
        }
        return validPaymentList;
    }, []);
};
/**
 * Converts a CustomerPaymentMethod to a display-ready format for UI rendering.
 *
 * This function processes payment method data from the Painter 2.0 API and transforms it into a standardized
 * format suitable for display in the payment selection UI. It handles various card types,
 * expiration date formatting, validation status, and localized messaging.
 *
 * @param paymentMethod - Raw payment method data from the API
 * @returns FilteredPaymentMethod object with formatted display properties
 * @throws Error if required payment method field is missing. Caller should catch this and not display the method.
 */
export const getDisplayReadyPaymentMethod = (paymentMethod) => {
    const cardDetails = paymentMethod.card;
    const [cardBrand, cardTypeTranslationKey] = buildCardDisplayName(cardDetails.cardBrand, cardDetails.cardType);
    const expirationDate = cardDetails.expirationDate;
    const cardExpirationDate = cardDetails.expirationStatus === ExpirationStatus.DoesNotExpire ? undefined : formatExpirationDate(expirationDate);
    const isInvalid = !paymentMethod.isValid || cardDetails.expirationStatus === ExpirationStatus.IsExpired;
    return {
        paymentMethodId: paymentMethod.paymentMethodId,
        cardBrand: cardBrand,
        cardTypeTranslationKey: cardTypeTranslationKey,
        cardExpirationStatus: cardDetails.expirationStatus,
        isInvalid: isInvalid,
        tail: cardDetails.tail,
        cardExpirationDate: cardExpirationDate,
        providedInvalidReason: paymentMethod.invalidMessage,
        cardImageUrl: cardDetails.cardImageUrl,
    };
};
/**
 * Builds card display components by extracting brand and type translation key.
 * Handles known cases where brand or type may be missing or invalid (e.g. AmericanExpress shows as UNKNOWN).
 *
 * @param cardBrand - Card brand (e.g., "Visa", "MasterCard")
 * @param cardType - Card type enum value (e.g., "CREDIT_CARD", "DEBIT_CARD")
 * @returns [cardBrand (empty string if not present), cardTypeTranslationKey]
 */
export const buildCardDisplayName = (cardBrand, cardType) => {
    const brand = cardBrand || '';
    const cardTypeTranslationKey = VALID_CARD_TYPES_TRANSLATION[cardType !== null && cardType !== void 0 ? cardType : ''] || (cardBrand ? '' : 'card');
    return [brand, cardTypeTranslationKey];
};
/**
 * Formats expiration date from API response to MM/YYYY display format.
 * Ensures single-digit months are zero-padded for consistent display.
 * Expiration date, month, and year must be present or function will throw error. Caller is expected to catch this.
 *
 * @param expirationDate - Expiration date object with month and year
 * @returns Formatted date string in MM/YYYY format
 */
export const formatExpirationDate = (expirationDate) => {
    return `${expirationDate.expirationMonth.toString().padStart(2, '0')}/${expirationDate.expirationYear.toString()}`;
};
/** Set by the 1-Click sheet's "Change payment method" link so the payment selection page returns to the PDP. */
export const ONE_CLICK_RETURN_PARAM = 'oneClickReturn';
/** Set on the PDP's URL so it re-opens the 1-Click sheet when it loads. */
export const ONE_CLICK_REOPEN_PARAM = 'oneClickReopen';
/** Sets or removes the PDP's 1-Click re-open flag, for `setSearchParams`. */
export const withOneClickReopen = (searchParams, reopen) => {
    if (reopen)
        searchParams.set(ONE_CLICK_REOPEN_PARAM, '1');
    else
        searchParams.delete(ONE_CLICK_REOPEN_PARAM);
    return searchParams;
};
/**
 * Returns the PDP path to leave the payment selection page by when it was opened from the 1-Click sheet
 * (`oneClickReturn=1`), or null to keep today's return to the Preview Order Page.
 *
 * The return flag is swapped for `oneClickReopen=1` and every other param (marketplace, creator attribution, …)
 * is kept. Without a bag ASIN there is no PDP to go back to, so it falls back to POP as today.
 */
export const getOneClickReturnPath = (search, asin) => {
    const searchParams = new URLSearchParams(search);
    if (searchParams.get(ONE_CLICK_RETURN_PARAM) !== '1' || !asin) {
        return null;
    }
    searchParams.delete(ONE_CLICK_RETURN_PARAM);
    searchParams.set(ONE_CLICK_REOPEN_PARAM, '1');
    return `${generatePath(routes.productDetailPage, { asin })}?${searchParams.toString()}`;
};
